import React, { useEffect, useState } from "react";
import {
  supportTicketService,
  SupportTicket,
} from "@core/services/SupportTicketService";
import { TicketSummaryCard } from "./TicketSummaryCard";
import { TicketSlidePanelMemo } from "./TicketSlidePanel";
import { TicketBulkActions, useTicketSelection } from "./TicketBulkActions";
import { SupportAIService } from "@core/services/SupportAIService";
import { AdminDebug } from "./AdminDebug";
import {
  SLAAlert,
  SLANotificationService,
} from "@core/services/SLANotificationService";
import { TestTicketCreator } from "./TestTicketCreator";
import {
  Search,
  Filter,
  Loader2,
  AlertCircle,
  LayoutDashboard,
  MessageSquare,
  CheckCircle2,
  TrendingUp,
  ShieldAlert,
  Clock,
  Users,
  BarChart3,
} from "lucide-react";

export const SupportDashboardSplit: React.FC = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<
    "all" | "open" | "in_progress" | "resolved" | "closed"
  >("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [slaMetrics, setSlaMetrics] = useState<any>(null);
  const [alerts, setAlerts] = useState<SLAAlert[]>([]);

  // Hook pour la sélection multiple
  const {
    selectedTickets,
    selectAll,
    toggleTicketSelection,
    toggleSelectAll,
    clearSelection,
    isTicketSelected,
  } = useTicketSelection();

  useEffect(() => {
    loadTickets();

    // Réactiver avec le nouveau système intelligent
    const slaService = SLANotificationService.getInstance();
    slaService.startMonitoring(15); // Vérifie toutes les 15 minutes

    // Nettoyer le monitoring quand le composant est démonté
    return () => {
      slaService.stopMonitoring();
    };
  }, [filter]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const statusFilter = filter === "all" ? undefined : [filter];

      const data = await supportTicketService.listTickets({
        status: statusFilter,
      });

      setTickets(data);
    } catch (error) {
      console.error("Failed to load tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  // Actions groupées
  const handleBulkStatusChange = async (
    ticketIds: string[],
    status: string,
  ) => {
    try {
      const updatePromises = ticketIds.map((ticketId) =>
        supportTicketService.updateTicket(ticketId, { status: status as any }),
      );

      await Promise.all(updatePromises);
      loadTickets(); // Recharger pour voir les changements
      clearSelection(); // Désélectionner après l'action
    } catch (error) {
      console.error("Failed to bulk update status:", error);
    }
  };

  const handleBulkAssign = async (ticketIds: string[], assignTo: string) => {
    try {
      const updatePromises = ticketIds.map((ticketId) =>
        supportTicketService.updateTicket(ticketId, {
          assigned_to: assignTo,
          assigned_at: new Date().toISOString(),
          assigned_by: assignTo,
        }),
      );

      await Promise.all(updatePromises);
      loadTickets(); // Recharger pour voir les changements
      clearSelection(); // Désélectionner après l'action
    } catch (error) {
      console.error("Failed to bulk assign:", error);
    }
  };

  const handleBulkDelete = async (ticketIds: string[]) => {
    try {
      const deletePromises = ticketIds.map((ticketId) =>
        supportTicketService.deleteTicket(ticketId),
      );

      await Promise.all(deletePromises);

      // Recharger les données seulement, pas la page entière
      await loadTickets();
      clearSelection(); // Désélectionner après l'action

      // Si le ticket sélectionné fait partie des tickets supprimés, désélectionner
      if (selectedTicketId && ticketIds.includes(selectedTicketId)) {
        setSelectedTicketId(null);
      }

      // Notification de succès
      console.log(`Successfully deleted ${ticketIds.length} tickets`);
    } catch (error) {
      console.error("Failed to bulk delete:", error);
      alert(
        "Erreur lors de la suppression groupée: " +
          (error instanceof Error ? error.message : String(error)),
      );
    }
  };

  const handleTicketUpdate = async (updatedTicket: SupportTicket | null) => {
    console.log("=== DASHBOARD TICKET UPDATE ===");
    console.log("Updated ticket received:", updatedTicket);

    if (updatedTicket) {
      // Mettre à jour le ticket dans la liste
      setTickets((prevTickets) => {
        const newTickets = prevTickets.map((t) =>
          t.id === updatedTicket.id ? updatedTicket : t,
        );
        console.log("Tickets updated in dashboard:", newTickets.length);
        return newTickets;
      });

      // Mettre à jour le ticket sélectionné si c'est celui qui est ouvert
      if (selectedTicketId === updatedTicket.id) {
        console.log("Updating selected ticket in panel");
        // Le TicketSlidePanel va recevoir le ticket mis à jour via ses props
      }

      // Log de confirmation
      console.log(`Ticket ${updatedTicket.id} updated successfully:`, {
        status: updatedTicket.status,
        assigned_to: updatedTicket.assigned_to,
        updated_at: updatedTicket.updated_at,
      });
    } else {
      console.log("No ticket returned, reloading full list...");
      // Si pas de ticket retourné, recharger la liste
      await loadTickets();
    }
  };

  const handleTestTicketCreated = (ticket: SupportTicket) => {
    console.log("🎫 [SupportDashboard] Test ticket created:", ticket.id);
    setTickets([ticket, ...tickets]);
    setSelectedTicketId(ticket.id);
  };

  const handleReopenTicket = async (ticketId: string) => {
    try {
      await supportTicketService.updateTicket(ticketId, { status: "open" });
      loadTickets(); // Recharger pour voir les changements
    } catch (error) {
      console.error("Failed to reopen ticket:", error);
    }
  };

  const handleDeleteTicket = async (ticketId: string) => {
    if (
      !confirm(
        "Êtes-vous sûr de vouloir supprimer ce ticket ? Cette action est irréversible.",
      )
    ) {
      return;
    }

    try {
      // Supprimer définitivement le ticket de la base de données
      await supportTicketService.deleteTicket(ticketId);

      loadTickets(); // Recharger pour voir les changements

      // Si le ticket supprimé était sélectionné, désélectionner
      if (selectedTicketId === ticketId) {
        setSelectedTicketId(null);
      }
    } catch (error) {
      console.error("Failed to delete ticket:", error);
      alert(
        "Erreur lors de la suppression du ticket: " +
          (error instanceof Error ? error.message : String(error)),
      );
    }
  };

  const filteredTickets = tickets.filter((t) => {
    const matchesSearch =
      (t.subject?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (t.email?.toLowerCase() || "").includes(searchTerm.toLowerCase());

    const matchesType = typeFilter === "all" || t.type === typeFilter;
    const matchesPriority =
      priorityFilter === "all" ||
      (t.priority !== null &&
        t.priority !== undefined &&
        t.priority.toString() === priorityFilter);

    // TOUJOURS inclure le ticket sélectionné, peu importe les filtres
    const isSelected = t.id === selectedTicketId;

    return (matchesSearch && matchesType && matchesPriority) || isSelected;
  });

  // DÉSACTIVÉ EN URGENCE - SLA metrics
  // useEffect(() => {
  //   const loadSLAMetrics = async () => {
  //     try {
  //       const slaService = SLANotificationService.getInstance();
  //       const metrics = await slaService.calculateSLAMetrics();
  //       setSlaMetrics(metrics);
  //
  //       const alertList = await slaService.checkSLACompliance();
  //       setAlerts(alertList);
  //     } catch (error) {
  //       console.error("Failed to load SLA metrics:", error);
  //     }
  //   };

  //   loadSLAMetrics();
  //   const interval = setInterval(loadSLAMetrics, 30000); // Recharger toutes les 30s
  //   return () => clearInterval(interval);
  // }, [tickets]);

  const selectedTicket = React.useMemo(() => {
    if (!selectedTicketId || !tickets.length) return null;
    return tickets.find((t) => t.id === selectedTicketId) || null;
  }, [selectedTicketId, tickets]);

  return (
    <>
      <div className="min-h-screen-safe bg-gray-950">
        {/* Dashboard plein écran */}
        <div className="p-6">
          {/* Header */}
          <div className="flex flex-col gap-4 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-purple-400">
                <LayoutDashboard className="w-5 h-5" />
                <span className="text-sm font-medium uppercase tracking-widest">
                  Administration
                </span>
              </div>
              <a
                href="/admin/analytics"
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <BarChart3 className="w-4 h-4" />
                Analytics
              </a>
            </div>
            <h1 className="text-3xl font-bold text-white">Support Dashboard</h1>
          </div>

          {/* Filtres et recherche */}
          <div className="flex flex-wrap gap-4 mb-8">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un ticket..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 w-full"
              />
            </div>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
            >
              <option value="all">Tous types</option>
              <option value="bug">Bug</option>
              <option value="feature">Feature</option>
              <option value="question">Question</option>
              <option value="other">Autre</option>
            </select>

            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
            >
              <option value="all">Toutes priorités</option>
              <option value="0">Basse (0)</option>
              <option value="1">Normale (1)</option>
              <option value="2">Haute (2)</option>
              <option value="3">Critique (3)</option>
            </select>

            <TestTicketCreator onTicketCreated={handleTestTicketCreated} />
          </div>

          {/* SLA Metrics */}
          {slaMetrics && (
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-400" />
                  Métriques SLA
                </h2>
                <div className="text-sm text-gray-400">
                  <span
                    className={`font-medium ${
                      slaMetrics.complianceRate >= 95
                        ? "text-green-400"
                        : slaMetrics.complianceRate >= 80
                          ? "text-yellow-400"
                          : "text-red-400"
                    }`}
                  >
                    {slaMetrics.complianceRate}% conformité
                  </span>{" "}
                  ({slaMetrics.overdue}/{slaMetrics.total} en retard)
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-3 rounded-lg bg-gray-800/50 border border-gray-700">
                  <div className="text-2xl font-bold text-white">
                    {slaMetrics.total}
                  </div>
                  <div className="text-xs text-gray-400">Total actifs</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-green-900/20 border border-green-800">
                  <div className="text-2xl font-bold text-green-400">
                    {slaMetrics.withinSLA}
                  </div>
                  <div className="text-xs text-gray-400">Dans les délais</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-red-900/20 border border-red-800">
                  <div className="text-2xl font-bold text-red-400">
                    {slaMetrics.overdue}
                  </div>
                  <div className="text-xs text-gray-400">En retard</div>
                </div>
              </div>
            </div>
          )}

          {/* Filtres rapides */}
          <div className="flex gap-2 mb-6">
            {["all", "open", "in_progress", "resolved", "closed"].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s as any)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  filter === s
                    ? "bg-purple-600 text-white shadow-lg shadow-purple-500/25"
                    : "bg-gray-800 text-gray-400 hover:bg-gray-700 border border-gray-700"
                }`}
              >
                {s === "all"
                  ? "Tous"
                  : s.charAt(0).toUpperCase() + s.slice(1).replace("_", " ")}
              </button>
            ))}
          </div>

          {/* Liste des tickets */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
              <span className="ml-3 text-gray-400">
                Chargement des tickets...
              </span>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="bg-gray-800/50 border border-gray-700 rounded-2xl p-12 text-center">
              <AlertCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                Aucun ticket trouvé
              </h3>
              <p className="text-gray-400">
                Modifiez vos filtres ou effectuez une nouvelle recherche.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Select all */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleSelectAll(filteredTickets)}
                  className="p-1.5 rounded border-2 transition-colors"
                >
                  {selectAll ? (
                    <svg
                      className="w-4 h-4 text-purple-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    </svg>
                  )}
                </button>
                <span className="text-sm text-gray-400">
                  {selectedTickets.length > 0 &&
                    `${selectedTickets.length} sélectionné${selectedTickets.length > 1 ? "s" : ""}`}
                </span>
              </div>

              {/* Tickets */}
              {filteredTickets.map((ticket) => (
                <div key={ticket.id} className="relative">
                  <div className="absolute top-2 left-2 z-10">
                    <button
                      onClick={() => toggleTicketSelection(ticket)}
                      className={`p-1.5 rounded border-2 transition-colors ${
                        isTicketSelected(ticket.id)
                          ? "bg-purple-500 border-purple-500 text-white"
                          : "bg-gray-800 border-gray-600 text-gray-400 hover:border-purple-400"
                      }`}
                    >
                      {isTicketSelected(ticket.id) ? (
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <rect
                            x="3"
                            y="3"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          />
                        </svg>
                      )}
                    </button>
                  </div>

                  {/* Actions pour tous les tickets */}
                  <div className="absolute top-2 right-2 z-10 flex gap-1">
                    {/* Bouton Réouvrir - seulement pour tickets fermés */}
                    {ticket.status === "closed" && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReopenTicket(ticket.id);
                        }}
                        className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        title="Réouvrir le ticket"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                      </button>
                    )}
                    {/* Bouton Supprimer - pour tous les tickets */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTicket(ticket.id);
                      }}
                      className="p-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                      title="Supprimer le ticket"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>

                  <TicketSummaryCard
                    ticket={ticket}
                    onClick={(id) => setSelectedTicketId(id)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Actions groupées */}
        <TicketBulkActions
          selectedTickets={selectedTickets}
          onClearSelection={clearSelection}
          onBulkStatusChange={handleBulkStatusChange}
          onBulkAssign={handleBulkAssign}
          onBulkDelete={handleBulkDelete}
        />
      </div>

      {/* Modal pour les détails du ticket */}
      {selectedTicket ? (
        <TicketSlidePanelMemo
          ticketId={selectedTicketId}
          ticket={selectedTicket}
          onClose={() => setSelectedTicketId(null)}
          onTicketUpdate={handleTicketUpdate}
          onTicketDeleted={(ticketId) => {
            // Ne PAS fermer la modale ici, TicketSlidePanelMemo le fait déjà
            loadTickets();
          }}
        />
      ) : null}
    </>
  );
};
